import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { MapService } from 'src/app/core/services/map.service'
import {
  CircleLayerSpecification,
  ExpressionSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl'
import { DataNonDetectionsService } from '../services/data-non-detections.service'

import { emptyGeoJsonSource } from 'src/app/core/utils/source-utils'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { NavigationStart, Router } from '@angular/router'
import { MapEventsService } from 'src/app/core/services/map-events.service'

@Injectable()
export class LayerNonDetectionsService implements OnDestroy {
  public isLayerVisible = false
  private readonly cleanUp$ = new Subject<void>()
  private historyEnabled = false

  public layerId = this.layer
  public sourceId = `${this.layerId}-src`
  public countLabelLayerId = `${this.layerId}-count-label`
  private nonDetectionsCircleLayerSpecification: CircleLayerSpecification = {
    id: this.layerId,
    source: this.sourceId,
    minzoom: this.minZoom,
    type: 'circle',
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 8, 5, 11, 20, 15, 100, 18],
      'circle-opacity': 0,
      'circle-stroke-color': '#08AEC4',
      'circle-stroke-opacity': 1,
      'circle-stroke-width': [
        'interpolate',
        ['linear'],
        ['get', 'count'],
        1,
        2,
        5,
        3,
        20,
        5,
        100,
        8,
      ],
    },
  }
  private nonDetectionsCountLabelLayerSpecification: SymbolLayerSpecification = {
    id: this.countLabelLayerId,
    source: this.sourceId,
    minzoom: this.minZoom,
    type: 'symbol',
    layout: {
      'text-field': ['to-string', ['get', 'count']],
      'text-anchor': 'center',
      'text-allow-overlap': true,
      'text-size': 15,
    },
    paint: {
      'text-color': '#08AEC4',
    },
  }

  constructor(
    private router: Router,
    private readonly mapService: MapService,
    private readonly mapEventsService: MapEventsService,
    private appState: AppStateService,
    @Inject(String) private layer: string,
    @Inject(Number) private minZoomAvailable: number,
    @Inject(Number) private maxZoomAvailable: number,
    @Inject(Number) private minZoom: number,
    private dataService: DataNonDetectionsService,
  ) {
    this.historyEnabled = this.appState.isHistoryEnabled()
    if (mapService.isMapReady) {
      this.initializeLayer()
    } else {
      this.mapService.mapReadyChanged.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
        this.initializeLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      this.evaluateState()
    })

    this.router.events.pipe(takeUntil(this.cleanUp$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.ngOnDestroy()
      }
    })
  }

  ngOnDestroy(): void {
    this.cancelRequests()
    this.cleanUp$.next()
    this.cleanUp$.complete()
    this.mapEventsService.detachMapEvents(this)
    this.mapService.map.removeLayer(this.countLabelLayerId)
    this.mapService.map.removeLayer(this.layerId)
    this.mapService.map.removeSource(this.sourceId)
  }

  private initializeLayer() {
    this.mapService.map.addSource(this.sourceId, {
      ...emptyGeoJsonSource,
      promoteId: 'clusterId',
    })
    this.mapService.map.addLayer(this.nonDetectionsCircleLayerSpecification)
    this.mapService.map.addLayer(this.nonDetectionsCountLabelLayerSpecification)
    this.mapEventsService.registerForMapEvents(this, true)
    this.evaluateState()
  }

  public async reload() {
    this.cancelRequests()
    if (
      this.mapService.isMapReady &&
      this.isLayerVisible &&
      this.mapService.map.getZoom() >= this.minZoom
    ) {
      const xyzTiles = this.mapService.getXyzTiles(this.maxZoomAvailable, this.minZoomAvailable)
      await this.dataService.loadNonDetectionsData(
        this.appState.isHistoryEnabled(),
        xyzTiles,
        this.appState.selectTimestamp(),
        this.mapService.getExpandedBounds(0.5),
      )
      this.updateLayer()
    }
  }

  public updateLayer() {
    if (this.mapService.isMapReady) {
      this.mapService.setGeoJsonData(this.sourceId, this.dataService.getNonDetections())
    }
  }

  private evaluateState() {
    this.updateSelectionHighlight()
    const historyEnabled = this.appState.isHistoryEnabled()
    const historyModeChanged = historyEnabled !== this.historyEnabled
    this.historyEnabled = historyEnabled

    if (this.appState.selectLayers().some((layer) => layer === this.layer)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }

    if (this.isLayerVisible === false) return

    if (!this.dataService.isDataInitialized) {
      this.reload()
      return
    }
    if (
      this.appState.hasMapBoundsChanged() ||
      (historyEnabled && this.appState.hasTimestampChanged()) ||
      historyModeChanged
    ) {
      this.reload()
    }
  }

  private updateSelectionHighlight(): void {
    if (!this.mapService.isMapReady || !this.mapService.map.getLayer(this.layerId)) return

    const selectedFeatureId = this.appState.getState().selectedFeatureId
    const isSelected: ExpressionSpecification = [
      'all',
      ['!=', selectedFeatureId ?? '', ''],
      ['==', ['to-string', ['get', 'clusterId']], selectedFeatureId ?? ''],
    ]
    const color: ExpressionSpecification = ['case', isSelected, '#FFC107', '#08AEC4']
    const sortKey: ExpressionSpecification = ['case', isSelected, 1, 0]

    this.mapService.map.setLayoutProperty(this.layerId, 'circle-sort-key', sortKey)
    this.mapService.map.setLayoutProperty(this.countLabelLayerId, 'symbol-sort-key', sortKey)
    this.mapService.map.setLayoutProperty(this.countLabelLayerId, 'symbol-z-order', 'source')
    this.mapService.map.setPaintProperty(this.layerId, 'circle-stroke-color', color)
    this.mapService.map.setPaintProperty(this.layerId, 'circle-color', '#FFC107')
    this.mapService.map.setPaintProperty(this.layerId, 'circle-opacity', [
      'case',
      isSelected,
      0.2,
      0,
    ])
    this.mapService.map.setPaintProperty(this.countLabelLayerId, 'text-color', color)
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      if (this.mapService.getLayerVisibilty(this.layerId) != this.isLayerVisible) {
        this.mapService.setLayerVisibility(this.layerId, isVisible)
        this.mapService.setLayerVisibility(this.countLabelLayerId, isVisible)
        if (!isVisible) {
          this.cancelRequests()
        }
      }
    }
  }

  private cancelRequests() {
    this.dataService.cancelCurrentRequests()
  }
}
